import React, { useState } from "react";
import { Box, Typography, Grid, CardContent, CardActionArea, Snackbar, Modal, Alert, Button } from "@mui/material";
import { styled } from "@mui/system";
import BackupIcon from "@mui/icons-material/CloudUpload";
import RestoreIcon from "@mui/icons-material/Restore";
import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import axios from 'axios';

const StyledCard = styled("div")(({ darkMode }) => ({
  backgroundColor: darkMode ? "#2A2A3C" : "#fff",
  borderRadius: "12px",
  boxShadow: darkMode ? "0 2px 10px rgba(0,0,0,0.6)" : "0 2px 10px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: darkMode ? "#383C54" : "#f5f5f5",
  },
}));

const StyledTypography = styled(Typography)(({ darkMode }) => ({
  color: darkMode ? "#EDEDED" : "#142be1",
}));

const StyledIcon = styled("div")(({ darkMode }) => ({
  color: darkMode ? "#EDEDED" : "#142be1",
  fontSize: 40,
  marginBottom: 8,
  display: "inline-flex",
  justifyContent: "center",
}));

const palette = {
  light: {
    headerBg: "#e3f2fd",
    headerText: "#37474f",
    rowHover: "#f5f5f5",
    text: "#333",
  },
  dark: {
    headerBg: "#2A2A3C",
    headerText: "#EDEDED",
    rowHover: "#383C54",
    text: "#EDEDED",
  },
};

const Backup = () => {
  const { darkMode } = useContext(ThemeContext);
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const currentUserRole = sessionStorage.getItem("userRole");
  const apiUrl = process.env.REACT_APP_BASE_URL
  const token = sessionStorage.getItem("authToken");

    // Snackbar handlers
  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

   {/* Add handler functions for backup modal and API call, with other handler functions */}
    const handleOpenBackupModal = () => {
      setBackupModalOpen(true);
    };
  
    const handleCloseBackupModal = () => {
      setBackupModalOpen(false);
    };

    const handleOpenRestoreModal = () => {
      setRestoreModalOpen(true);
    }

    const handleCloseRestoreModal = () => {
      setRestoreModalOpen(false);
    }
  
    // Handle backup confirmation and API call
const handleBackupConfirm = async () => {
  try {
    const token = sessionStorage.getItem("authToken");
    const response = await axios.post(`${apiUrl}/api/backup/`,
      null,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setSnackbar({
      open: true,
      message: response.data.message || "Backup initiated successfully.",
      severity: "success",
    });
    handleCloseBackupModal();
  } catch (error) {
    console.error("Failed to initiate backup:", error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || "Failed to initiate backup. Please try again.",
      severity: "error",
    });
    handleCloseBackupModal();
  }
};

// Handle restore confirmation and API call
  const handleRestoreConfirm = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await axios.post(
        `${apiUrl}/api/restore/`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSnackbar({
        open: true,
        message: response.data.message || "Restore initiated successfully.",
        severity: "success",
      });
      handleCloseRestoreModal();
    } catch (error) {
      console.error("Failed to initiate restore:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to initiate restore. Please try again.",
        severity: "error",
      });
      handleCloseRestoreModal();
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{
          color: darkMode ? palette.dark.headerText : "#142be1",
          fontWeight: "bold",
          mb: 3,
          textAlign: "center",
        }}
      >
        Backup Panel
      </Typography>

      {/* mongo db backup card */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
           {currentUserRole === "admin" && (
        <Grid item xs={12} sm={4}>
          <StyledCard darkMode={darkMode}>
            <CardActionArea onClick={handleOpenBackupModal}>
              <CardContent sx={{ textAlign: "center" }}>
                <StyledIcon darkMode={darkMode}>
                  <BackupIcon sx={{ fontSize: 40, mb: 1 }} />
                </StyledIcon>
                <StyledTypography darkMode={darkMode} variant="h6">
                  Mongo DB Backup
                </StyledTypography>
              </CardContent>
            </CardActionArea>
          </StyledCard>
        </Grid>
      )}
      {/* mongo db restore card */}
       {currentUserRole === "admin" && (
        <Grid item xs={12} sm={4}>
          <StyledCard darkMode={darkMode}>
            <CardActionArea onClick={handleOpenRestoreModal}>
              <CardContent sx={{ textAlign: "center" }}>
                <StyledIcon darkMode={darkMode}>
                  <BackupIcon sx={{ fontSize: 40, mb: 1 }} />
                </StyledIcon>
                <StyledTypography darkMode={darkMode} variant="h6">
                  Mongo DB Restore
                </StyledTypography>
              </CardContent>
            </CardActionArea>
          </StyledCard>
        </Grid>
      )}
      </Grid>
       {/* Backup confirmation modal */}
        <Modal
          open={backupModalOpen}
          onClose={handleCloseBackupModal}
          aria-labelledby="backup-modal-title"
          aria-describedby="backup-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography
              id="backup-modal-title"
              variant="h6"
              sx={{ color: darkMode ? palette.dark.headerText : "#142be1", fontWeight: "500" }}
            >
              Confirm Backup
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to take backup of your entire flashcard?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseBackupModal}
                sx={{
                  color: darkMode ? palette.dark.headerText : "#142be1",
                  borderColor: darkMode ? palette.dark.headerText : "#142be1",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleBackupConfirm}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#388e3c" },
                }}
              >
                Confirm Backup
              </Button>
            </Box>
          </Box>
        </Modal>
          {/* Restore Confirmation Modal */}
      <Modal
        open={restoreModalOpen}
        onClose={handleCloseRestoreModal}
        aria-labelledby="restore-modal-title"
        aria-describedby="restore-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography
            id="restore-modal-title"
            variant="h6"
            sx={{ color: darkMode ? palette.dark.headerText : "#142be1", fontWeight: "500" }}
          >
            Confirm Restore
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to restore your flashcard database? This action may overwrite existing data.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseRestoreModal}
              sx={{
                color: darkMode ? palette.dark.headerText : "#142be1",
                borderColor: darkMode ? palette.dark.headerText : "#142be1",
                textTransform: "none",
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRestoreConfirm}
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#388e3c" },
              }}
            >
              Confirm Restore
            </Button>
          </Box>
        </Box>
      </Modal>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Box>
  );
};

export default Backup;
